import { useQuery } from '@tanstack/react-query';
import { metaApi, MessagingUsageSummary, MessagingLimitInfo } from '@/lib/api/services/meta';

export interface MessagingUsageData {
  tier: string;
  quota: number | null; // null means unlimited
  used: number;
  remaining: number | null; // null means unlimited
  windowStart: string;
  windowEnd: string;
  canSend: boolean;
  phoneNumber: string | null;
  qualityRating: string | null;
}

// Re-export types for backward compatibility
export type { MessagingLimitInfo, MessagingUsageSummary };

export function useMessagingUsage(businessAccountId: string | null | undefined) {
  return useQuery<MessagingUsageSummary | MessagingUsageData>({
    queryKey: ['messaging-usage', businessAccountId],
    queryFn: async () => {
      if (!businessAccountId) {
        throw new Error('Business account ID is required');
      }

      try {
        const response = await metaApi.getMessagingUsage(businessAccountId);

        if (!response || !response.data) {
          throw new Error('Empty response from messaging usage API');
        }

        const data = response.data;

        // If the backend already returns the structured format (with phoneNumbers array), return it directly
        if (data.phoneNumbers && Array.isArray(data.phoneNumbers)) {
          return data as MessagingUsageSummary;
        }

        // Fallback for flat structure (if legacy)
        // Transform the new flat structure to match the expected MessagingUsageSummary format
        const aggregate: MessagingLimitInfo = {
          tier: data.tier || null,
          limit: data.quota,
          isUnlimited: data.quota === null,
          used: data.used || 0,
          remaining: data.remaining,
          windowStart: data.windowStart || null,
          quality_rating: data.qualityRating || null,
        };

        const phoneNumber: MessagingUsagePhoneNumber = {
          id: businessAccountId,
          display_phone_number: data.phoneNumber || null,
          messaging_limit_tier: data.tier || null,
          quality_rating: data.qualityRating || null,
          status: data.status || null,
          messaging_limit_info: aggregate,
        };

        const result = {
          businessAccountId,
          phoneNumbers: [phoneNumber],
          aggregate,
        } as MessagingUsageSummary;

        return result;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!businessAccountId,
    refetchInterval: 30000, // Refetch every 30s to keep usage stats live
    staleTime: 10000, // Consider data stale after 10 seconds
    refetchOnWindowFocus: true, // Refresh immediately when user returns to tab
    retry: 2, // Retry failed requests twice
  });
}

// Helper type for phone number
interface MessagingUsagePhoneNumber {
  id: string;
  display_phone_number: string | null;
  status?: string | null;
  verified_name?: string | null;
  messaging_limit_tier?: string | null;
  messaging_limit_info?: MessagingLimitInfo | null;
  quality_rating?: string | null;
}
